import type { CarouselModel } from '@snowit/types/carousels';
import type { CartModel } from '@snowit/types/carts';
import type { CategoryModel } from '@snowit/types/categories';
import type { ChallengeModel } from '@snowit/types/challenges';
import type { CheckoutCodeModel } from '@snowit/types/checkoutCodes';
import type { CouponModel } from '@snowit/types/coupons';
import type { DealerOrderModel } from '@snowit/types/dealers';
import type { DepartureModel } from '@snowit/types/departures';
import type { DiscountModel } from '@snowit/types/discounts';
import type { DistrictModel } from '@snowit/types/districts';
import type { BrevoEmailTransaction } from '@snowit/types/emails';
import type { ExportRequestsModel } from '@snowit/types/exportRequests';
import type { ExternalCodeModel } from '@snowit/types/externalCodes';
import type { ImportModel, SyncStatusModel } from '@snowit/types/imports';
import type {
  MembershipFilters,
  MembershipModel,
  MembershipTransitModel,
} from '@snowit/types/memberships';
import type { NotificationModel } from '@snowit/types/notifications';
import type { ProductOptionModel } from '@snowit/types/options';
import type { OrderModel } from '@snowit/types/orders';
import type { PartnerModel } from '@snowit/types/partners';
import type { PolicyModel } from '@snowit/types/policies';
import type { PriceModel } from '@snowit/types/prices';
import type { ProductFilters, ProductModel } from '@snowit/types/products';
import type { ProviderOrderModel } from '@snowit/types/providers';
import type { ReviewModel } from '@snowit/types/reviews';
import type { RouteModel } from '@snowit/types/routes';
import type { SeasonModel } from '@snowit/types/seasons';
import type { MonitoringCounters } from '@snowit/types/shared';
import type { ShippingMethodModel } from '@snowit/types/shippingMethods';
import type { SkipassPromoModel } from '@snowit/types/skipass_promos';
import type { CardModel } from '@snowit/types/snowitcards';
import type { StageModel } from '@snowit/types/stages';
import type { TagModel } from '@snowit/types/tags';
import type { CloudTaskErrorModel } from '@snowit/types/tasks';
import type { TenantGroups, TenantMessageModel, TenantModel } from '@snowit/types/tenants';
import type { TurnstileModel } from '@snowit/types/turnstiles';
import type { UserModel } from '@snowit/types/users';
import type { VenueModel } from '@snowit/types/venues';
import type { ZoneModel } from '@snowit/types/zones';

export const ALL_LOADERS = [
  'products_get_one',
  'products_get',
  'products_get_next',
  'products_get_variants',
  'products_get_slots',
  'products_write',
  'products_write_variant',
  'products_write_slot',
  'products_delete_variant',
  'products_delete_slot',
  'products_duplicate',
  'products_update_hotel',
  //
  'options_get',
  'options_get_one',
  'options_get_next',
  'options_write',
  //
  'prices_get',
  'prices_get_one',
  'prices_write',
  //
  'seasons_get',
  'seasons_get_one',
  'seasons_write',
] as const;

export type Loaders = (typeof ALL_LOADERS)[number];

export type StoreProducts = {
  list: ProductModel[];
  details?: ProductModel;
  filters?: ProductFilters;
  next?: Function;
};

export type StorePrices = {
  list: PriceModel[];
  next?: Function;
};

export type StoreOptions = {
  list: ProductOptionModel[];
  ready: boolean;
};

export type StoreMemberships = {
  list: MembershipModel[];
  details?: MembershipModel;
  filters: MembershipFilters;
  next?: Function;
};

export type StoreSnowitcards = {
  list: CardModel[];
  details?: CardModel;
  next?: Function;
};

export type StoreExternalCodes = {
  list: ExternalCodeModel[];
  count?: number;
  next?: Function;
};

export type StoreCheckoutCodes = {
  list: CheckoutCodeModel[];
  count?: number;
  next?: Function;
};

export type StoreCoupons = {
  list: CouponModel[];
  next?: Function;
};

export type StoreSeasons = {
  list: SeasonModel[];
  ready?: boolean;
};

export type StoreTags = {
  list: TagModel[];
  ready: boolean;
};

export type StoreBrevo = {
  list: BrevoEmailTransaction[];
  email?: string;
};

export type StoreTenants = {
  list: TenantModel[];
  messages: TenantMessageModel[];
  ready: boolean;
  // selected?: string;
  active: string[];
  bookmarks: string[];
  groups: TenantGroups;
};

export type StoreZones = {
  list: ZoneModel[];
  details?: ZoneModel;
  ready: boolean;
};

export type StoreDistricts = {
  list: DistrictModel[];
  ready: boolean;
};

export type StoreDepartures = {
  list: DepartureModel[];
  ready: boolean;
};

export type StoreShippingMethods = {
  list: ShippingMethodModel[];
  ready: boolean;
};

export type StoreCategories = {
  list: CategoryModel[];
  ready: boolean;
};

export type StoreVenues = {
  list: VenueModel[];
  ready: boolean;
};

export type StoreProductOptions = {
  list: ProductOptionModel[];
};

export type StorePartners = {
  list: PartnerModel[];
  selected?: string;
  ready: boolean;
};

export type StoreSettings = {
  theme: string;
  fontsize: string;
};

export type StoreMonitoring = {
  data: {
    providers: {
      list: ProviderOrderModel[];
      next?: Function;
    };
    netstorming: {
      list: ProviderOrderModel[];
      next?: Function;
    };
    mailers: {
      list: CloudTaskErrorModel[];
      next?: Function;
    };
    invoice: {
      list: CloudTaskErrorModel[];
      next?: Function;
    };
    orders_sync: {
      list: CloudTaskErrorModel[];
      next?: Function;
    };
    orders_sepa: {
      list: OrderModel[];
      next?: Function;
    };
    orders_processing: {
      list: OrderModel[];
      next?: Function;
    };
    failed_transits: {
      list: MembershipTransitModel[];
      next?: Function;
    };
    membership_assignment_status: {
      list: MembershipModel[];
      next?: Function;
    };
    membership_unpaid: {
      list: MembershipModel[];
      next?: Function;
    };
    membership_blocked: {
      list: MembershipModel[];
      next?: Function;
    };
  };
  counters: MonitoringCounters;
  trenordCounters: Record<string, number>;
  retryAt: number;
};

export type StoreProviderOrders = {
  list: ProviderOrderModel[];
  next?: Function;
};

export type StoreTrenitaliaOrders = {
  list: ProviderOrderModel[];
  next?: Function;
};

export type StoreDealerOrders = {
  list: DealerOrderModel[];
  next?: Function;
};

export type StoreDiscounts = {
  list: DiscountModel[];
  next?: Function;
};

export type StoreOrders = {
  list: OrderModel[];
  next?: Function;
};

export type StoreSkipassPromos = {
  list: SkipassPromoModel[];
  next?: Function;
};

export type StoreSkipassGroups = {
  list: SkipassPromoModel[];
  next?: Function;
};

export type StoreStages = {
  list: StageModel[];
  next?: Function;
};
export type StoreRoutes = {
  list: RouteModel[];
  next?: Function;
};

export type StoreCarousels = {
  list: CarouselModel[];
};

export type StoreCarts = {
  list: CartModel[];
  next?: Function;
};

export type StoreNotifications = {
  list: NotificationModel[];
  next?: Function;
};

export type StoreReviews = {
  list: ReviewModel[];
  next?: Function;
};

export type StorePolicies = {
  list: PolicyModel[];
  ready?: boolean;
};

export type StoreImports = {
  list: ImportModel[];
  syncStatus: SyncStatusModel[];
  next?: Function;
};

export type ExportRequests = {
  list: ExportRequestsModel[];
  next?: Function;
};

export type StoreTransits = {
  list: MembershipTransitModel[];
  next?: Function;
};

export type StoreTurnstiles = {
  list: TurnstileModel[];
  next?: Function;
};

export type StoreChallenges = {
  list: ChallengeModel[];
  next?: Function;
  details?: ChallengeModel;
};

export type StoreUsers = {
  list: UserModel[];
  next?: Function;
  selected?: string;
};

export type Store = {
  brevo: StoreBrevo;
  carousels: StoreCarousels;
  carts: StoreCarts;
  categories: StoreCategories;
  challenges: StoreChallenges;
  checkout_codes: StoreCheckoutCodes;
  coupons: StoreCoupons;
  dealer_orders: StoreDealerOrders;
  departures: StoreDepartures;
  discounts: StoreDiscounts;
  districts: StoreDistricts;
  external_codes: StoreExternalCodes;
  loaders: Record<Loaders, boolean>;
  memberships: StoreMemberships;
  monitoring: StoreMonitoring;
  notifications: StoreNotifications;
  options: StoreOptions;
  orders: StoreOrders;
  partners: StorePartners;
  policies: StorePolicies;
  prices: StorePrices;
  products: StoreProducts;
  provider_orders: StoreProviderOrders;
  reviews: StoreReviews;
  routes: StoreRoutes;
  seasons: StoreSeasons;
  settings: StoreSettings;
  shipping_methods: StoreShippingMethods;
  skipass_promos: StoreSkipassPromos;
  skipass_groups: StoreSkipassGroups;
  snowitcards: StoreSnowitcards;
  stages: StoreStages;
  tags: StoreTags;
  tenants: StoreTenants;
  venues: StoreVenues;
  zones: StoreZones;
  imports: StoreImports;
  trenitalia_orders: StoreTrenitaliaOrders;
  transits: StoreTransits;
  turnstiles: StoreTurnstiles;
  exportRequests: ExportRequests;
  users: StoreUsers;
};

export type StoreFn = <K extends keyof Store>(
  k: K,
  v: Partial<Store[K]> | ((input: Store[K]) => Store[K]),
) => void;

export type StoreWithActions = {
  store: Store;
  loaders: { [x in Loaders]?: boolean };
  setStore: StoreFn;
  setLoader: (k: Loaders, v: boolean) => void;
};
